import { _ as _defineProperty } from '../../unsorted/defineProperty.js';
import { _ as _slicedToArray } from '../../unsorted/slicedToArray.js';
import { _ as _objectWithoutProperties } from '../../unsorted/objectWithoutProperties.js';
import { useMemo, useState, useCallback, useEffect } from 'react';
import { m as merge } from '../../unsorted/index.js';
import { _ as _toConsumableArray } from '../../unsorted/toConsumableArray.js';
import { getResponsiveSx, getStartingSize } from '../../utils/styleHelpers.js';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import '@emotion/react';
import { S as Stack } from '../Stack/Stack.js';
import { R as RollingText } from '../RollingText/RollingText.js';
import { u as useWindowSizeStore } from '../../providers/windowSizeContext/windowSizeContext.js';
import { useTheme } from '../../hooks/useTheme.js';
import { B as Body } from '../Body/Body.js';
import { H as Heading } from '../Heading/Heading.js';
import { useTimeout } from '../../hooks/useTimeout.js';
import { useInterval } from '../../hooks/useInterval.js';
import { useBrowserEffect } from '../../hooks/useBrowserEffect.js';
import { warnUser } from '../../utils/componentHelpers.js';
import { useGetCurrentSizeClass } from '../../hooks/useGetCurrentSizeClass.js';

var COUNTDOWN_TIMER_SIZES = ["small", "medium", "large", "xLarge"];
var DEFAULT_COUNTDOWN_TIMER_SIZE = COUNTDOWN_TIMER_SIZES[1];
var COUNTDOWN_TIMER_VARIANTS = ["primary", "inverse"];
var DEFAULT_COUNTDOWN_TIMER_VARIANT = COUNTDOWN_TIMER_VARIANTS[0];

function ownKeys$2(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$2(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$2(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$2(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var countDownContainerBaseSx = {
  flexShrink: 0,
  flexGrow: 0,
  flexWrap: "nowrap",
  position: "relative"
};
var baseTileSx = {
  backdropFilter: "blur(28px)",
  "& .Heading, & .Body": {
    c: "inherit"
  }
};
function getTileSx(_ref) {
  var size = _ref.size,
    theme = _ref.theme;
  switch (size) {
    case "small":
      return {
        width: theme.base.spacing.x12,
        height: theme.base.spacing.x8,
        borderRadius: theme.base.borderRadius.x2
      };
    case "large":
      return {
        width: theme.base.spacing.x16,
        height: theme.base.spacing.x16,
        borderRadius: theme.base.borderRadius.x6
      };
    case "xLarge":
      return {
        width: theme.base.spacing.x20,
        height: theme.base.spacing.x20,
        borderRadius: theme.base.borderRadius.x6
      };
    case "medium":
    default:
      return {
        width: theme.base.spacing.x12,
        height: theme.base.spacing.x12,
        borderRadius: theme.base.borderRadius.x4
      };
  }
}
var getResponsiveTileSx = function getResponsiveTileSx(props) {
  return getResponsiveSx(_objectSpread$2(_objectSpread$2({}, props), {}, {
    renderSxAtSize: getTileSx
  }));
};
function getTileVariantSx(variant) {
  switch (variant) {
    case "inverse":
      return {
        bg: "base.color.translucent.inverse.600",
        c: "base.color.text.body.primary"
      };
    case "primary":
    default:
      return {
        bg: "base.color.translucent.standard.100",
        c: "base.color.text.body.inverse.primary"
      };
  }
}

var _excluded$1 = ["digit", "label", "size", "variant", "sx"];
function ownKeys$1(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function pickLabelFontWeight(size) {
  switch (size) {
    case "xLarge":
      return "bold";
    case "small":
    case "medium":
    case "large":
    default:
      return "regular";
  }
}
function pickLabelFontSize(size) {
  switch (size) {
    case "large":
      return "xSmall";
    case "xLarge":
      return "small";
    case "small":
    case "medium":
    default:
      return "xxSmall";
  }
}
function pickDigitComponent(size) {
  switch (size) {
    case "small":
      return jsx(Body, {
        size: "xxSmall",
        weight: "bold"
      });
    case "large":
      return jsx(Body, {
        size: "large",
        weight: "bold"
      });
    case "xLarge":
      return jsx(Heading, {
        size: "medium",
        weight: "bold"
      });
    case "medium":
    default:
      return jsx(Body, {
        size: "small",
        weight: "bold"
      });
  }
}
function usePickDigitComponent(size) {
  var _useWindowSizeStore = useWindowSizeStore(function (state) {
      return state.width;
    }),
    width = _useWindowSizeStore.state;
  var _useTheme = useTheme(),
    breakpointAsArray = _useTheme.base.breakpointAsArray;
  var picked = useMemo(function () {
    var defaultSize = pickDigitComponent("medium");
    if (width === null) return defaultSize;
    if (Array.isArray(size)) {
      var actualResponsiveSizes = _toConsumableArray(size);
      actualResponsiveSizes.shift();
      return actualResponsiveSizes.reduce(function (accum, curr, index) {
        var newAccum = accum;
        if (curr !== null && width >= breakpointAsArray[index]) {
          newAccum = pickDigitComponent(curr);
        }
        return newAccum;
      }, defaultSize);
    }
    return /small|medium|large|xLarge/.test(size) ? pickDigitComponent(size) : undefined;
  }, [breakpointAsArray, size, width]);
  return picked;
}
function CountdownTile(_ref) {
  var digit = _ref.digit,
    label = _ref.label,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_COUNTDOWN_TIMER_SIZE : _ref$size,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? DEFAULT_COUNTDOWN_TIMER_VARIANT : _ref$variant,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    props = _objectWithoutProperties(_ref, _excluded$1);
  var theme = useTheme();
  var prettyDigit = useMemo(function () {
    return digit < 10 ? "0".concat(digit) : digit;
  }, [digit]);
  var pickedDigitUseComponent = usePickDigitComponent(size);
  var startingSize = getStartingSize(size, DEFAULT_COUNTDOWN_TIMER_SIZE, COUNTDOWN_TIMER_SIZES);
  var allSx = merge(baseTileSx, getTileVariantSx(variant), getTileSx({
    size: startingSize,
    theme: theme
  }), getResponsiveTileSx({
    size: size,
    theme: theme
  }), sx);
  var labelTextSize = useMemo(function () {
    if (Array.isArray(size)) {
      return size.map(function (s) {
        return pickLabelFontSize(s);
      });
    }
    return pickLabelFontSize(size);
  }, [size]);
  var labelFontWeight = useMemo(function () {
    if (Array.isArray(size)) {
      return size.map(function (s) {
        return pickLabelFontWeight(s);
      });
    }
    return pickLabelFontWeight(size);
  }, [size]);
  return jsxs(Stack, _objectSpread$1(_objectSpread$1({}, props), {}, {
    alignItems: "center",
    justifyContent: "center",
    gap: "0px",
    sx: allSx,
    className: "CountdownTile CountdownTile--".concat(label),
    testId: "CountdownTimer__Tile--".concat(label),
    children: [jsx(RollingText, {
      text: prettyDigit.toString(),
      use: pickedDigitUseComponent,
      testId: "CountdownTimer__tile__textRoller"
    }), jsx(Body, {
      size: labelTextSize,
      weight: labelFontWeight,
      testId: "CountdownTimer__tile__label",
      children: label
    })]
  }));
}

var _excluded = ["futureTarget", "hideDays", "size", "variant", "className", "testId", "onCountdownEnd", "direction", "justifyContent", "displayUpdateInterval", "sx"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function getTimeLeft(futureTarget) {
  var diffTime = Math.abs(new Date().valueOf() - new Date(futureTarget).valueOf());
  var days = diffTime / (24 * 60 * 60 * 1000);
  var hours = days % 1 * 24;
  var minutes = hours % 1 * 60;
  var seconds = minutes % 1 * 60;
  return {
    days: days,
    hours: hours,
    minutes: minutes,
    seconds: seconds
  };
}
var DEFAULT_TIME = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0
};
function CountdownTimer(_ref) {
  var futureTarget = _ref.futureTarget,
    _ref$hideDays = _ref.hideDays,
    hideDays = _ref$hideDays === void 0 ? false : _ref$hideDays,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_COUNTDOWN_TIMER_SIZE : _ref$size,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? DEFAULT_COUNTDOWN_TIMER_VARIANT : _ref$variant,
    className = _ref.className,
    testId = _ref.testId,
    onCountdownEnd = _ref.onCountdownEnd,
    _ref$direction = _ref.direction,
    direction = _ref$direction === void 0 ? "row" : _ref$direction,
    _ref$justifyContent = _ref.justifyContent,
    justifyContent = _ref$justifyContent === void 0 ? "space-between" : _ref$justifyContent,
    _ref$displayUpdateInt = _ref.displayUpdateInterval,
    displayUpdateInterval = _ref$displayUpdateInt === void 0 ? 1000 : _ref$displayUpdateInt,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(DEFAULT_TIME),
    _useState2 = _slicedToArray(_useState, 2),
    timeLeft = _useState2[0],
    setTimeLeft = _useState2[1];
  useBrowserEffect(function () {
    setTimeLeft(getTimeLeft(futureTarget));
    return function () {
      clearInterval(displayPollIntervalRef.current);
      clearTimeout(timoutRef.current);
    };
  }, []);
  var cancelIntervalAndResetTime = useCallback(function () {
    setTimeLeft(DEFAULT_TIME);
    clearInterval(displayPollIntervalRef.current);
  }, []);

  // @NOTE: setup a single timeout to fire the callback when the countdown ends:
  var now = new Date().valueOf();
  var future = futureTarget.valueOf();
  var diff = future - now;
  var timoutRef = useTimeout(function () {
    onCountdownEnd === null || onCountdownEnd === void 0 ? void 0 : onCountdownEnd();
    cancelIntervalAndResetTime();
  }, diff);

  // @NOTE: create a slow interval to update the component's time display:
  var displayPollIntervalRef = useInterval(function () {
    var newTimeLeft = getTimeLeft(futureTarget);
    var now = new Date();
    if (now >= futureTarget) {
      cancelIntervalAndResetTime();
    }
    return setTimeLeft(newTimeLeft);
  }, displayUpdateInterval);
  useEffect(function () {
    var now = new Date();
    if (now >= futureTarget) {
      warnUser("CountdownTimer has recieved ".concat(futureTarget.toLocaleString(), " as a futureTarget. This is invalid, as it is in the past.\n  Timer interval has been disabled"));
      clearInterval(displayPollIntervalRef.current);
      setTimeLeft(DEFAULT_TIME);
    }
  }, [futureTarget, displayPollIntervalRef]);
  var sizeClass = useGetCurrentSizeClass(size, DEFAULT_COUNTDOWN_TIMER_SIZE, COUNTDOWN_TIMER_SIZES);
  var allSx = useMemo(function () {
    return merge(countDownContainerBaseSx, sx);
  }, [sx]);
  return jsxs(Stack, _objectSpread(_objectSpread({}, props), {}, {
    direction: direction,
    justifyContent: justifyContent,
    testId: testId,
    sx: allSx,
    className: "".concat(className !== null && className !== void 0 ? className : "", " CountdownTimer CountdownTimer--").concat(variant, " CountdownTimer--").concat(sizeClass),
    children: [!hideDays && jsx(CountdownTile, {
      label: "days",
      digit: Math.floor(timeLeft.days),
      size: size,
      variant: variant
    }), jsx(CountdownTile, {
      label: "hours",
      digit: Math.floor(timeLeft.hours),
      size: size,
      variant: variant
    }), jsx(CountdownTile, {
      label: "minutes",
      digit: Math.floor(timeLeft.minutes),
      size: size,
      variant: variant
    }), jsx(CountdownTile, {
      label: "seconds",
      digit: Math.floor(timeLeft.seconds),
      size: size,
      variant: variant
    })]
  }));
}
CountdownTimer.displayName = "CountdownTimer";

export { CountdownTimer as C, DEFAULT_COUNTDOWN_TIMER_SIZE as D, COUNTDOWN_TIMER_SIZES as a, COUNTDOWN_TIMER_VARIANTS as b, DEFAULT_COUNTDOWN_TIMER_VARIANT as c };
//# sourceMappingURL=CountdownTimer.js.map
